import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, ThemeProvider } from "@mui/material";
import { CHAT_PAGE_FULL_PATH } from "@src/appV2/Chat/paths";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { BottomSheet, Text, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { getTheme } from "@src/appV2/redesign/system/ui-theme";
import { generatePath, useHistory } from "react-router-dom";

import { Button } from "../components/Button";
import { DialogFooter } from "../components/DialogFooter";
import { TextButton } from "../components/TextButton";
import { TopRightCloseButton } from "../components/TopRightCloseButton";
import { type ChartingNoncompliance } from "./api/schema";
import { chartingDeadlineHasPassed } from "./utils/helpers";

interface ChartingNoncomplianceRectifiedBottomSheetProps {
  chartingNoncomplianceRectifiedModalState: UseModalState;
  chartingNoncomplianceReviewModalState: UseModalState;
  workerId: string;
  workplaceName: string;
  workplaceId: string;
  chartingNoncompliance: ChartingNoncompliance;
}

export function ChartingNoncomplianceRectifiedBottomSheet({
  chartingNoncomplianceRectifiedModalState,
  chartingNoncomplianceReviewModalState,
  workerId,
  workplaceName,
  workplaceId,
  chartingNoncompliance,
}: ChartingNoncomplianceRectifiedBottomSheetProps) {
  const history = useHistory();

  const logMetadata = {
    timestamp: new Date().toISOString(),
    workerId,
    workplaceId,
  };

  const {
    attributes: { deadlineToReview },
  } = chartingNoncompliance;

  if (
    isDefined(deadlineToReview) &&
    !chartingDeadlineHasPassed(deadlineToReview) &&
    chartingNoncomplianceRectifiedModalState.modalIsOpen
  ) {
    chartingNoncomplianceRectifiedModalState.closeModal();
    chartingNoncomplianceReviewModalState.openModal();
    return null;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <BottomSheet
        modalState={chartingNoncomplianceRectifiedModalState}
        canDismiss={false}
        footer={
          <DialogFooter orientation="vertical">
            <Button
              size="large"
              sx={{ width: "100%" }}
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.CHARTING_NONCOMPLIANCE_WORKPLACE_MESSAGED, logMetadata);
                chartingNoncomplianceRectifiedModalState.closeModal();
                chartingNoncomplianceReviewModalState.openModal();
              }}
            >
              I am inside the workplace
            </Button>

            <TextButton
              underline
              size="large"
              sx={{ lineHeight: 1.5, padding: 4 }}
              onClick={() => {
                logEvent(APP_V2_USER_EVENTS.CHARTING_NONCOMPLIANCE_INSIDE_WORKPLACE, logMetadata);
                chartingNoncomplianceRectifiedModalState.closeModal();
                history.push(generatePath(CHAT_PAGE_FULL_PATH, { facilityId: workplaceId }));
              }}
            >
              I left, notify the workplace
            </TextButton>
          </DialogFooter>
        }
      >
        <DialogContent sx={{ px: 4 }}>
          <TopRightCloseButton onClick={chartingNoncomplianceRectifiedModalState.closeModal} />
        </DialogContent>
        <Box
          sx={{
            paddingX: 4,
            paddingTop: 8,
            paddingBottom: 4,
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          <Text sx={{ fontWeight: "bold", textAlign: "center", paddingX: 8 }} variant="h3">
            Verify Charting
          </Text>
          <Text sx={{ textAlign: "center", lineHeight: 1, padding: 4 }} variant="body1">
            Confirm your status so {workplaceName} can verify your charting as complete
          </Text>
        </Box>
      </BottomSheet>
    </ThemeProvider>
  );
}
